/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  useDisclosure,
  Text,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { ViewIcon } from '@chakra-ui/icons';
import { QuestionnaireTemplateListResponse } from '../../questionnaireTemplate/questionnaireTemplate.types';
import { QuestionnaireTemplateSelect } from './QuestionnaireTemplateSelect';
// import { TemplateSelectedBanner } from './TemplateSelectedBanner';
// import { TemplateCantFindBanner } from './TemplateCantFindBanner';
import { QuestionnaireTemplateModal } from '../../questionnaireTemplate/modal/QuestionnaireTemplateModal';

type QuestionTemplateSectionProps = {
  selectedTemplate?: QuestionnaireTemplateListResponse;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<QuestionnaireTemplateListResponse | undefined>>;
  handleQuestionTemplateChange?: (template: QuestionnaireTemplateListResponse | undefined) => void;
  setQuestionTemplates: React.Dispatch<React.SetStateAction<QuestionnaireTemplateListResponse[]>>;
  questionTemplates: QuestionnaireTemplateListResponse[];
  // jobPositionTemplateId?: string | null;
  hasQuestions?: boolean;
  saveScrollPosition?: () => void;
  restoreScrollPosition?: () => void;
};

export const QuestionTemplateSection = ({
  selectedTemplate,
  setSelectedTemplate,
  handleQuestionTemplateChange,
  questionTemplates,
  setQuestionTemplates,
  // jobPositionTemplateId,
  hasQuestions = false,
  saveScrollPosition,
  restoreScrollPosition,
}: QuestionTemplateSectionProps) => {
  const { t } = useTranslation();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [pendingTemplate, setPendingTemplate] = React.useState<QuestionnaireTemplateListResponse | undefined>(undefined);
  const [overrideType, setOverrideType] = React.useState<'questions' | 'template' | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const focusRef = React.useRef<HTMLDivElement>(null);
  const selectRef = React.useRef<HTMLButtonElement>(null);
  const applyTemplate = (template?: QuestionnaireTemplateListResponse) => {
    setSelectedTemplate(template);
    handleQuestionTemplateChange?.(template);
  };
  const onTemplateSelect = (template: QuestionnaireTemplateListResponse | undefined) => {
    if (!template) {
      applyTemplate(undefined);
      return;
    }

    if (selectedTemplate?.id === template.id) {
      return;
    }

    saveScrollPosition?.();

    if (hasQuestions && !selectedTemplate) {
      setPendingTemplate(template);
      setOverrideType('questions');
      onConfirmOpen();
      restoreScrollPosition?.();
      return;
    }
    if (selectedTemplate && selectedTemplate.id !== template.id) {
      setPendingTemplate(template);
      setOverrideType('template');
      onConfirmOpen();
      restoreScrollPosition?.();

      return;
    }

    applyTemplate(template);
    requestAnimationFrame(() => {
      restoreScrollPosition?.();
    });
  };
  const handleConfirm = () => {
    applyTemplate(pendingTemplate);
    setPendingTemplate(undefined);
    setOverrideType(null);
    onConfirmClose();
    // Restore scroll after template applied
    requestAnimationFrame(() => {
      restoreScrollPosition?.();
      focusRef.current?.focus();
    });
  };
  const handleCancel = () => {
    setPendingTemplate(undefined);
    setOverrideType(null);
    onConfirmClose();

    requestAnimationFrame(() => {
      restoreScrollPosition?.();
    });
  };

  return (
    <Box borderRadius="12px" border="1px solid var(--Brand-50, #E4F5FF)" bg="#F7FAFC" p={4} gap={4}>
      <Box w="60%">
        <Heading as="h3" fontSize="md" lineHeight="1.2" mb={1}>
          {t('createJobPosition:newQuestionHeader')}
        </Heading>
        <Box fontSize="xs" color="gray.500" lineHeight="normal">
          {t('createJobPosition:questionsHint')}
        </Box>
      </Box>

      <HStack my={4} justifyContent="space-between">
        <Box w="50%" ref={focusRef} tabIndex={-1} outline="none">
          <QuestionnaireTemplateSelect
            ref={selectRef}
            value={selectedTemplate?.id ?? undefined}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={onTemplateSelect}
            setQuestionTemplates={setQuestionTemplates}
            questionTemplates={questionTemplates}
          />
        </Box>
        <Box>
          {selectedTemplate && (
            <HStack spacing={3}>
              <Button size="sm" leftIcon={<ViewIcon />} variant="ghost" bg="gray.100" borderRadius="20px" onClick={onPreviewOpen}>
                <Text fontSize="sm" fontWeight="400">
                  {t('questionnaireTemplate:formBuilder.preview.title')}
                </Text>
              </Button>
            </HStack>
          )}
        </Box>
      </HStack>
      <Divider my={2} />
      <Flex flexDirection="column">
        {/* {selectedTemplate && (
          <TemplateSelectedBanner
            templateName={selectedTemplate.name}
            templateLocale={selectedTemplate.questionLocale}
            onOpen={onOpen}
            setSelectedTemplate={setSelectedTemplate}
          />
        )} */}
        {/* <Box mt={jobPositionTemplateId ? 4 : 0}>
          <TemplateCantFindBanner />
        </Box> */}
        <QuestionnaireTemplateModal
          isOpen={isPreviewOpen}
          onClose={onPreviewClose}
          templates={selectedTemplate ? [selectedTemplate] : []}
          finalFocusRef={focusRef}
        />

        <AlertDialog
          isOpen={isConfirmOpen}
          leastDestructiveRef={cancelRef}
          onClose={handleCancel}
          isCentered
          finalFocusRef={selectRef}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t('createJobPosition:templateSelectionConfirmation:confirmTemplateChange')}
              </AlertDialogHeader>

              <AlertDialogBody>
                {overrideType === 'questions' && t('createJobPosition:templateSelectionConfirmation.overrideQuestionsWarning')}
                {overrideType === 'template' && t('createJobPosition:templateSelectionConfirmation.overrideTemplateWarning')}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button colorScheme="red" ref={cancelRef} onClick={handleCancel}>
                  {t('createJobPosition:templateSelectionConfirmation.cancel')}
                </Button>

                <Button colorScheme="blue" onClick={handleConfirm} ml={3}>
                  {t('createJobPosition:templateSelectionConfirmation.proceed')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </Box>
  );
};
